<template>
  <div class="files-container">
    <div class="files-header">
      <div class="search-filter">
        <input 
          type="text" 
          v-model="searchQuery" 
          placeholder="Search files..." 
          @input="filterFiles"
          class="search-input"
        />
      </div>
      <div class="file-filters">
        <div class="filter-title">Filter by:</div>
        <div class="filter-options">
          <label class="filter-option">
            <input type="checkbox" v-model="filters.certificates" @change="filterFiles">
            <span>Certificates</span>
          </label>
          <label class="filter-option">
            <input type="checkbox" v-model="filters.reports" @change="filterFiles">
            <span>Reports</span>
          </label>
          <label class="filter-option">
            <input type="checkbox" v-model="filters.images" @change="filterFiles">
            <span>Images</span>
          </label>
          <label class="filter-option">
            <input type="checkbox" v-model="filters.documents" @change="filterFiles">
            <span>Documents</span>
          </label>
        </div>
      </div>
      <div class="upload-button">
        <button class="btn-upload" @click="showUploadModal = true">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
          Upload File
        </button>
      </div>
    </div>
    
    <div class="files-list">
      <div v-if="filteredFiles.length === 0" class="no-files">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
        <p>No files found matching your criteria</p>
      </div>
      
      <div v-for="file in filteredFiles" :key="file.id" class="file-item" @click="selectFile(file)">
        <div class="file-icon" :class="getFileIconClass(file.type)">
          <svg v-if="file.type === 'certificate'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="8" r="7"></circle>
            <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
          </svg>
          <svg v-else-if="file.type === 'report'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
            <polyline points="10 9 9 9 8 9"></polyline>
          </svg>
          <svg v-else-if="file.type === 'image'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <circle cx="8.5" cy="8.5" r="1.5"></circle>
            <polyline points="21 15 16 10 5 21"></polyline>
          </svg>
          <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
            <polyline points="13 2 13 9 20 9"></polyline>
          </svg>
        </div>
        <div class="file-info">
          <div class="file-name">{{ file.name }}</div>
          <div class="file-meta">
            <span class="file-type">{{ file.type }}</span>
            <span class="file-date">{{ formatDate(file.date) }}</span>
            <span class="file-size">{{ formatSize(file.size) }}</span>
          </div>
        </div>
        <div class="file-actions">
          <button class="btn-download" @click.stop="downloadFile(file)">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
          </button>
        </div>
      </div>
    </div>
    
    <!-- File Preview Modal -->
    <div v-if="selectedFile" class="file-preview-modal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ selectedFile.name }}</h3>
          <button class="close-btn" @click="selectedFile = null">&times;</button>
        </div>
        <div class="modal-body">
          <div v-if="selectedFile.type === 'image'" class="image-preview">
            <img :src="selectedFile.url" :alt="selectedFile.name">
          </div>
          <div v-else-if="selectedFile.type === 'certificate'" class="certificate-preview">
            <div class="certificate-header">
              <h2>Certificate of Origin</h2>
              <div class="certificate-id">{{ selectedFile.metadata.id }}</div>
            </div>
            <div class="certificate-content">
              <div class="certificate-field">
                <div class="field-label">Product</div>
                <div class="field-value">{{ selectedFile.metadata.product }}</div>
              </div>
              <div class="certificate-field">
                <div class="field-label">Origin</div>
                <div class="field-value">{{ selectedFile.metadata.origin }}</div>
              </div>
              <div class="certificate-field">
                <div class="field-label">Date Issued</div>
                <div class="field-value">{{ formatDate(selectedFile.date) }}</div>
              </div>
              <div class="certificate-field">
                <div class="field-label">Issuing Authority</div>
                <div class="field-value">{{ selectedFile.metadata.authority }}</div>
              </div>
            </div>
            <div class="certificate-footer">
              <div class="certificate-stamp">VERIFIED</div>
            </div>
          </div>
          <div v-else class="document-preview">
            <div class="document-content">
              <p>{{ selectedFile.preview || 'Preview not available for this file type.' }}</p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-download" @click="downloadFile(selectedFile)">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Download
          </button>
        </div>
      </div>
    </div>
    
    <!-- Upload Modal -->
    <div v-if="showUploadModal" class="upload-modal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Upload File</h3>
          <button class="close-btn" @click="showUploadModal = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="upload-area" @click="triggerFileInput" @dragover.prevent="onDragOver" @drop.prevent="onDrop">
            <input type="file" ref="fileInput" style="display: none" @change="onFileSelected">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
            <p>Click or drag file to upload</p>
            <p v-if="uploadFile" class="selected-file">Selected: {{ uploadFile.name }}</p>
          </div>
          
          <div class="file-metadata">
            <div class="form-group">
              <label>File Type</label>
              <select v-model="uploadFileType">
                <option value="certificate">Certificate</option>
                <option value="report">Report</option>
                <option value="image">Image</option>
                <option value="document">Document</option>
              </select>
            </div>
            <div class="form-group">
              <label>Related To</label>
              <input type="text" v-model="uploadFileRelatedTo" placeholder="e.g., KOO-25-001">
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="showUploadModal = false">Cancel</button>
          <button class="btn-upload" @click="uploadSelectedFile" :disabled="!uploadFile">Upload</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    searchResult: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      files: [],
      filteredFiles: [],
      selectedFile: null,
      searchQuery: '',
      filters: {
        certificates: true,
        reports: true,
        images: true,
        documents: true
      },
      showUploadModal: false,
      uploadFile: null,
      uploadFileType: 'document',
      uploadFileRelatedTo: ''
    };
  },
  watch: {
    searchResult: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.generateFilesList();
        }
      }
    }
  },
  methods: {
    generateFilesList() {
      // Generate sample files based on search result
      this.files = [];
      
      if (this.searchResult && this.searchResult.results && this.searchResult.results.length > 0) {
        const result = this.searchResult.results[0];
        
        // Add certificate files
        this.files.push({
          id: 'cert-1',
          name: `${result.awta_certificate || 'AWTA-' + result.code}.pdf`,
          type: 'certificate',
          date: '2023-04-15',
          size: 1240000,
          url: '#',
          metadata: {
            id: result.awta_certificate || 'AWTA-' + result.code,
            product: result.type || 'Merino wool fleece',
            origin: result.origin_farm || 'Kookaburra Plains',
            authority: 'Australian Wool Testing Authority'
          }
        });
        
        this.files.push({
          id: 'cert-2',
          name: `Origin_Certificate_${result.code || 'KOO-25-001'}.pdf`,
          type: 'certificate',
          date: '2023-03-20',
          size: 980000,
          url: '#',
          metadata: {
            id: `OC-${result.code || 'KOO-25-001'}`,
            product: result.type || 'Merino wool fleece',
            origin: result.origin_farm || 'Kookaburra Plains',
            authority: 'Australian Wool Authority'
          }
        });
        
        // Add report files
        this.files.push({
          id: 'report-1',
          name: `Quality_Report_${result.code || 'KOO-25-001'}.pdf`,
          type: 'report',
          date: '2023-04-10',
          size: 2450000,
          url: '#',
          preview: 'This quality report details the fiber diameter, strength, and other key characteristics of the wool batch. The sample tested meets all premium grade requirements with an average fiber diameter of 19.5 microns.'
        });
        
        this.files.push({
          id: 'report-2',
          name: `Processing_Report_${result.code || 'KOO-25-001'}.pdf`,
          type: 'report',
          date: '2023-05-05',
          size: 1850000,
          url: '#',
          preview: 'Processing report for wool batch processing at Sydney Wool Exchange. Batch was sorted, graded, and prepared for export according to international standards. No contamination was detected.'
        });
        
        // Add image files
        this.files.push({
          id: 'image-1',
          name: `Wool_Sample_${result.code || 'KOO-25-001'}.jpg`,
          type: 'image',
          date: '2023-03-15',
          size: 3200000,
          url: 'https://www.woolmark.com/globalassets/woolmark/inspiration--education/fibre/australian-merino-wool/australian-merino-wool-hero.jpg'
        });
        
        this.files.push({
          id: 'image-2',
          name: `Farm_${result.origin_farm || 'Kookaburra_Plains'}.jpg`,
          type: 'image',
          date: '2023-02-20',
          size: 4100000,
          url: 'https://www.woolmark.com/globalassets/woolmark/inspiration--education/fibre/wool-growing-regions/australia-wool-growing-regions.jpg'
        });
        
        // Add document files
        this.files.push({
          id: 'doc-1',
          name: `Export_Documentation_${result.export_consignment || 'CNS-CHN-A'}.pdf`,
          type: 'document',
          date: '2023-05-10',
          size: 1650000,
          url: '#',
          preview: 'Export documentation for consignment to China. Includes customs declarations, packing lists, and commercial invoice. All regulatory requirements have been met for export.'
        });
        
        this.files.push({
          id: 'doc-2',
          name: `Traceability_Record_${result.code || 'KOO-25-001'}.pdf`,
          type: 'document',
          date: '2023-05-12',
          size: 2100000,
          url: '#',
          preview: 'Complete traceability record for the wool batch from farm to factory. Includes all handling, processing, and transportation details with timestamps and responsible parties.'
        });
      }
      
      // Apply initial filtering
      this.filterFiles();
    },
    
    filterFiles() {
      this.filteredFiles = this.files.filter(file => {
        const matchesSearch = this.searchQuery === '' || 
          file.name.toLowerCase().includes(this.searchQuery.toLowerCase());
        
        const matchesFilter = 
          (file.type === 'certificate' && this.filters.certificates) ||
          (file.type === 'report' && this.filters.reports) ||
          (file.type === 'image' && this.filters.images) ||
          (file.type === 'document' && this.filters.documents);
        
        return matchesSearch && matchesFilter;
      });
    },
    
    selectFile(file) {
      this.selectedFile = file;
    },
    
    downloadFile(file) {
      // In a real application, this would trigger a file download
      alert(`Downloading file: ${file.name}`);
    },
    
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    },
    
    formatSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
      else return (bytes / 1048576).toFixed(1) + ' MB';
    },
    
    getFileIconClass(type) {
      return `file-icon-${type}`;
    },
    
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    
    onFileSelected(event) {
      const file = event.target.files[0];
      if (file) {
        this.uploadFile = file;
      }
    },
    
    onDragOver(event) {
      event.target.classList.add('drag-over');
    },
    
    onDrop(event) {
      event.target.classList.remove('drag-over');
      const file = event.dataTransfer.files[0];
      if (file) {
        this.uploadFile = file;
      }
    },
    
    uploadSelectedFile() {
      if (!this.uploadFile) return;
      
      // In a real application, this would upload the file to a server
      const newFile = {
        id: `upload-${Date.now()}`,
        name: this.uploadFile.name,
        type: this.uploadFileType,
        date: new Date().toISOString().split('T')[0],
        size: this.uploadFile.size,
        url: '#',
        metadata: {
          id: `NEW-${Date.now()}`,
          product: 'Uploaded file',
          origin: this.uploadFileRelatedTo || 'Unknown',
          authority: 'User Upload'
        }
      };
      
      this.files.unshift(newFile);
      this.filterFiles();
      this.showUploadModal = false;
      this.uploadFile = null;
      this.uploadFileType = 'document';
      this.uploadFileRelatedTo = '';
      
      alert(`File uploaded: ${newFile.name}`);
    }
  }
};
</script>

<style scoped>
.files-container {
  display: flex;
  flex-direction: column;
  height: 600px;
  position: relative;
}

.files-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 12px 16px;
  background-color: rgb(245, 248, 247);
  border-radius: 8px 8px 0 0;
  border-bottom: 1px solid #e5e7eb;
  gap: 16px;
}

.search-filter {
  flex: 1;
  min-width: 200px;
}

.search-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
}

.file-filters {
  display: flex;
  align-items: center;
}

.filter-title {
  font-weight: 600;
  font-size: 14px;
  color: #4b5563;
  margin-right: 16px;
}

.filter-options {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.filter-option {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.filter-option input {
  margin-right: 6px;
}

.upload-button {
  margin-left: auto;
}

.btn-upload {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: #f97316;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-upload:hover {
  background-color: #ea580c;
}

.files-list {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background-color: #f9fafb;
  border-radius: 0 0 8px 8px;
}

.no-files {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #6b7280;
}

.file-item {
  display: flex;
  align-items: center;
  padding: 12px;
  background-color: white;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.2s;
}

.file-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transform: translateY(-2px);
}

.file-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  margin-right: 16px;
  color: white;
}

.file-icon-certificate {
  background-color: #8b5cf6;
}

.file-icon-report {
  background-color: #3b82f6;
}

.file-icon-image {
  background-color: #10b981;
}

.file-icon-document {
  background-color: #f97316;
}

.file-info {
  flex: 1;
}

.file-name {
  font-weight: 500;
  font-size: 14px;
  color: #111827;
  margin-bottom: 4px;
}

.file-meta {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #6b7280;
}

.file-actions {
  display: flex;
  gap: 8px;
}

.btn-download {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background-color: #f3f4f6;
  border: none;
  border-radius: 6px;
  color: #4b5563;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-download:hover {
  background-color: #e5e7eb;
}

/* Modal styles */
.file-preview-modal,
.upload-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
}

.modal-body {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
}

.modal-footer {
  padding: 16px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* File preview styles */
.image-preview {
  display: flex;
  justify-content: center;
  align-items: center;
}

.image-preview img {
  max-width: 100%;
  max-height: 60vh;
  object-fit: contain;
}

.certificate-preview {
  border: 2px solid #8b5cf6;
  border-radius: 8px;
  padding: 24px;
}

.certificate-header {
  text-align: center;
  margin-bottom: 24px;
}

.certificate-header h2 {
  margin: 0 0 8px 0;
  color: #8b5cf6;
}

.certificate-id {
  font-size: 18px;
  font-weight: 600;
}

.certificate-content {
  margin-bottom: 24px;
}

.certificate-field {
  margin-bottom: 16px;
  display: flex;
}

.field-label {
  width: 150px;
  font-weight: 600;
  color: #4b5563;
}

.field-value {
  flex: 1;
}

.certificate-footer {
  display: flex;
  justify-content: flex-end;
}

.certificate-stamp {
  display: inline-block;
  padding: 8px 16px;
  background-color: #8b5cf6;
  color: white;
  font-weight: 600;
  border-radius: 4px;
  transform: rotate(-5deg);
}

.document-preview {
  padding: 16px;
  background-color: #f9fafb;
  border-radius: 8px;
}

/* Upload modal styles */
.upload-area {
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  cursor: pointer;
  margin-bottom: 24px;
  transition: border-color 0.2s;
}

.upload-area:hover,
.upload-area.drag-over {
  border-color: #f97316;
}

.selected-file {
  margin-top: 16px;
  font-weight: 500;
  color: #f97316;
}

.file-metadata {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-weight: 500;
  color: #4b5563;
}

.form-group input,
.form-group select {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
}

.btn-cancel {
  padding: 8px 16px;
  background-color: #f3f4f6;
  color: #4b5563;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-cancel:hover {
  background-color: #e5e7eb;
}
</style>
